import { useCallback, useEffect, useMemo, useState } from 'react';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { EventPropGetter, SlotInfo, View, Views } from 'react-big-calendar';
import { useGetAgendaEventsMutation, useUpdateAgendaEventMutation, useDeleteAgendaEventMutation, useUpdateAgendaEventStatusMutation } from 'services/agenda/agenda.endpoint';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { getUser } from 'features/auth/auth.slice';
import { useGetAllUsersMutation } from 'services/user/user.endpoint';
import { getDatalists } from 'features/datalist/datalist.slice';
import { User } from 'services/auth/auth.types';
import { AgendaEvent } from 'services/agenda/agenda.types';
import { useApiToast } from 'components/Toaster';
import { assignColorToUser } from 'utils/color';

export const useCalendar = () => {
    const [allEvents, setAllEvents] = useState<AgendaEvent[]>([]);
    const [eventDetail, setEventDetail] = useState<AgendaEvent>();
    const [usersFilter, setUsersFilter] = useState<{ label: string; value: number|string }[]>();
    const [createEventContent, setCreateEventContent] = useState<Partial<AgendaEvent>>();
    const [openCreateEventModal, setOpenCreateEventModal] = useState<boolean>(false);
    const [currentDate, setCurrentDate] = useState<{date: Date, view: View}>({date: new Date(), view: 'week'});
    const [usersList, setUsersList] = useState<User[]>([]);
    const [colors, setColors] = useState<string[]>([]);
    const [userColorMap, setUserColorMap] = useState<Record<string, string>>({});
    const [eventCreationStep, setEventCreationStep] = useState<number>(1);
    const [isDeleteAgendaEventModalOpen, setIsDeleteAgendaEventModalOpen] = useState<boolean>(false);
    const { datalists } = useSelector(getDatalists);
    const { showApiToast, showErrorToast } = useApiToast();


    useEffect(() => {
        if(!openCreateEventModal) {
            setCreateEventContent(undefined);
        }
    }, [openCreateEventModal]);


    const user = useSelector(getUser);

    
    useEffect(() => {
        if(user) {
            setUsersFilter([{label: user?.fullname, value: user?.id}]);
        }
    }, [user]);


    const [getAgendaEvents, { isLoading, isError, data }] = useGetAgendaEventsMutation();
    const [
        getAllUsers,
        {
            isLoading: isAllUsersLoading,
            isError: isAllUsersError,
            isSuccess: isAllUsersSuccess,
            error: allUsersError,
            data: allUsersResponse
        }
    ] = useGetAllUsersMutation();

    const [
        updateAgendaEvent,
        {
            isLoading: isUpdateAgendaEventLoading,
            isError: isUpdateAgendaEventError,
            data: updateAgendaEventData,
            isSuccess: isUpdateAgendaEventSuccess,
            error: updateAgendaEventError
        }
    ] = useUpdateAgendaEventMutation();

    const [
        deleteAgendaEvent,
        {
            isLoading: isDeleteAgendaEventLoading,
            isError: isDeleteAgendaEventError,
            data: deleteAgendaEventData,
            isSuccess: isDeleteAgendaEventSuccess,
            error: deleteAgendaEventError
        }
    ] = useDeleteAgendaEventMutation();

    const [
        updateAgendaEventStatus,
        {
            isLoading: isUpdateAgendaEventStatusLoading,
            isError: isUpdateAgendaEventStatusError,
            data: updateAgendaEventStatusData,
            isSuccess: isUpdateAgendaEventStatusSuccess,
            error: updateAgendaEventStatusError
        }
    ] = useUpdateAgendaEventStatusMutation();

    useEffect(() => {
        getAllUsers({});
    }, [getAllUsers]);


    useEffect(() => {
        if (!datalists?.agenda_colors) return;

        const userColor = user?.color;
        const availableColors = Object.values(datalists.agenda_colors).filter(color => color !== userColor);
        const newUserColorMap = { ...userColorMap };

        // Assigner la couleur de l'utilisateur courant en premier s'il en a une
        if (userColor && user?.id) {
            newUserColorMap[user.id.toString()] = userColor;
        }

        // Assigner les couleurs pour chaque utilisateur filtré
        usersFilter?.forEach(userFilter => {
            const userId = userFilter.value;
            const usedColors = Object.values(newUserColorMap);
            
            newUserColorMap[userId] = assignColorToUser(
                user?.id?.toString() || '',
                userId.toString(),
                userColor,
                availableColors,
                usedColors,
                newUserColorMap
            );
        });

        setUserColorMap(newUserColorMap);
        setColors(usersFilter?.map(userFilter => newUserColorMap[userFilter.value]) || []);
    }, [datalists?.agenda_colors, usersFilter]);

    useEffect(() => {
        if (isAllUsersLoading) return;
        if (isAllUsersError && allUsersError) return;
        if (isAllUsersSuccess && allUsersResponse) {
            setUsersList(allUsersResponse.data);
        }
    }, [isAllUsersLoading, isAllUsersError, allUsersError, allUsersResponse, isAllUsersSuccess, user]);

    useEffect(() => {
        if (isUpdateAgendaEventLoading) return;
        if (isUpdateAgendaEventError && updateAgendaEventError) {
            showErrorToast(updateAgendaEventError, {
                message: "Erreur lors de la mise à jour de l'évènement"
            });
        }
        if (isUpdateAgendaEventSuccess && updateAgendaEventData) {
            showApiToast(updateAgendaEventData, {
                successMessage: 'Évènement mis à jour avec succès',
                errorMessage: "Erreur lors de la mise à jour de l'évènement"
            });
        }
    }, [isUpdateAgendaEventLoading, isUpdateAgendaEventError, updateAgendaEventError, updateAgendaEventData, isUpdateAgendaEventSuccess, showErrorToast, showApiToast]);

    useEffect(() => {
        if (isDeleteAgendaEventLoading) return;
        if (isDeleteAgendaEventError && deleteAgendaEventError) {
            showErrorToast(deleteAgendaEventError, {
                message: "Erreur lors de la suppression de l'évènement"
            });
        }
        if (isDeleteAgendaEventSuccess && deleteAgendaEventData) {
            showApiToast(deleteAgendaEventData, {
                successMessage: 'Évènement supprimé avec succès',
                errorMessage: "Erreur lors de la suppression de l'évènement"
            });
        }
    }, [isDeleteAgendaEventLoading, isDeleteAgendaEventError, deleteAgendaEventError, deleteAgendaEventData, isDeleteAgendaEventSuccess, showErrorToast, showApiToast]);


    const fetchEvents = useCallback(
        ({date, view}: {date: Date, view: View}) => {
    
            const startDate = moment(date).startOf(view.toLowerCase() as moment.unitOfTime.StartOf).format('YYYY-MM-DD');
            const endDate = moment(date).endOf(view.toLowerCase() as moment.unitOfTime.StartOf).format('YYYY-MM-DD');
        
            const usersList = usersFilter?.map((u) => u.value).join(',');

            //if(!usersList) return;

            getAgendaEvents({
                started_at: startDate,
                ended_at: endDate,
                users: usersList
            });
        },
        [getAgendaEvents, usersFilter]
    );

    useEffect(() => {
        fetchEvents(currentDate);
    }, [fetchEvents, currentDate, usersFilter]);

    useEffect(() => {
        if (!isLoading && !isError && data) {
            const formattedEvents: AgendaEvent[] = data.data.map((event) => ({
                ...event,
                title: event.label,
                start: new Date(event.started_at),
                end: new Date(event.ended_at),
            }));

            setAllEvents(formattedEvents);
        }
    }, [data, isError, isLoading]);

    const moveOrResizeEvent = useCallback(
        async ({ event, start, end }: EventInteractionArgs<any>) => {
            setAllEvents((prev: AgendaEvent[]) => {
                const existing = prev.find((ev) => ev.id === event.id);
                const filtered = prev.filter((ev) => ev.id !== event.id);

                if (!existing) return prev;


                if(event.stakeholders_list) {
                    event.stakeholders = event.stakeholders_list.map((item: any  ) => Number(item.value));
                }
                if(event.assistants_list) {
                    event.assistants = event.assistants_list.map((item: any) => Number(item.value));
                }
                if(event.partners_list) {
                    event.partners = event.partners_list.map((item: any) => Number(item.value));
                }
                if(event.person && event.person.value) {
                    event.person_id = Number(event.person.value);
                }
                if(event.created_by && event.created_by.value) {
                    event.created_by_id = Number(event.created_by.value);
                }
                if(event.emails_list) {
                    event.emails = event.emails_list.map((item: any) => item.value);
                }
                updateAgendaEvent({
                    ...event,
                    started_at: moment(start).format('YYYY-MM-DDTHH:mm:ssZ'),
                    ended_at: moment(end).format('YYYY-MM-DDTHH:mm:ssZ')
                });

                return [
                    ...filtered,
                    {
                        ...event,
                        started_at: String(start),
                        ended_at: String(end),
                        start: new Date(start),
                        end: new Date(end),
                        title: event.label,
                    }
                ];
            });
        },
        [setAllEvents]
    );

    const handleDeleteAgendaEvent = () => {
        if (eventDetail && eventDetail.created_by_id === user?.id) {
            deleteAgendaEvent({ id: String(eventDetail.id) });
            setAllEvents((prev: AgendaEvent[]) => prev.filter((ev) => ev.id !== eventDetail.id));
        }
        setIsDeleteAgendaEventModalOpen(false);
        setEventDetail(undefined);
    };

    const handleSelectSlot = useCallback(({ start, end }: SlotInfo) => {
        setCreateEventContent({
            label: '',

            end_at_date: moment(end).format('YYYY-MM-DD'),
            end_at_hour: moment(end).format('HH'),
            end_at_minute: moment(end).format('mm'),
            ended_at: moment(end).format('YYYY-MM-DDTHH:mm:ssZ'),

            start_at_date: moment(start).format('YYYY-MM-DD'),
            start_at_hour: moment(start).format('HH'),
            start_at_minute: moment(start).format('mm'),
            started_at: moment(start).format('YYYY-MM-DDTHH:mm:ssZ'),

            start: new Date(start),
            end: new Date(end)
        });
        setOpenCreateEventModal(true);
    }, []);

    const handleEditEvent = useCallback((event: AgendaEvent) => {
        setCreateEventContent({
            ...event,
            end_at_date: moment(event.ended_at).format('YYYY-MM-DD'),
            end_at_hour: moment(event.ended_at).format('HH'),
            end_at_minute: moment(event.ended_at).format('mm'),
            ended_at: moment(event.ended_at).format('YYYY-MM-DDTHH:mm:ssZ'),

            start_at_date: moment(event.started_at).format('YYYY-MM-DD'),
            start_at_hour: moment(event.started_at).format('HH'),
            start_at_minute: moment(event.started_at).format('mm'),
            started_at: moment(event.started_at).format('YYYY-MM-DDTHH:mm:ssZ'),

            start: new Date(event.started_at),
            end: new Date(event.ended_at),
        });
        /*
            set eventType
            stackholders
            assistants
            person
            partners
        */
        setEventDetail(undefined)
        setEventCreationStep(1);
        setOpenCreateEventModal(true);
    }, []);

    const createEvent = (event: AgendaEvent) => {
        if (!event) return;
        setAllEvents((prev: AgendaEvent[]) => [...prev, { ...event, start: new Date(event.started_at), end: new Date(event.ended_at), title: event.label } as AgendaEvent]);
    };

    const defaultDate = useMemo(() => currentDate.date, [currentDate]);

    const eventStyleGetter: EventPropGetter<AgendaEvent> = (event) => {
        const userId = event.created_by_id.toString();
        const userColor = userColorMap[userId];

        return {
            style: {
                backgroundColor: userColor || '#3174ad',
                borderRadius: '5px',
                opacity: 0.8,
                color: 'white',
                border: '0px',
                display: 'block',
            }
        };
    };

    const escapeICSValue = (value: string) => {
        return value.replace(
            /[\\,;]/g,
            '\\$&'
        );
    }

    const getEventIcs = (event: AgendaEvent) => {
        return {
            title: escapeICSValue((datalists?.event_types?.[event?.event_type ?? ""] ?? " ") + " - " + event.label),
            description: escapeICSValue(event.observation ?? ""),
            startTime: moment(event.started_at).tz('Europe/Paris').format(),
            endTime: moment(event.ended_at).tz('Europe/Paris').format(), 
            location: escapeICSValue(event.address?.formatted ?? "")
        };
    };


    const handleStatusEvent = (event: AgendaEvent, status: string) => {
        updateAgendaEventStatus({ id: String(event.id), status });

        if(status === 'canceled' || status === 'refused') {
            setAllEvents((prev: AgendaEvent[]) => prev.filter((ev) => ev.id !== event.id));
        }else{
            setAllEvents((prev: AgendaEvent[]) => prev.map((ev) => ev.id === event.id ? { ...ev, status } : ev));
        }

        setIsDeleteAgendaEventModalOpen(false);
        setEventDetail(undefined);
    };

    return {
        setAllEvents,
        events: allEvents,
        isLoading,
        defaultDate,
        moveOrResizeEvent,
        eventStyleGetter,
        handleSelectSlot,
        createEventContent,
        createEvent,
        openCreateEventModal,
        setOpenCreateEventModal,
        handleEditEvent,
        eventDetail,
        setEventDetail,
        usersFilter,
        setUsersFilter,
        setCurrentDate,
        currentDate,
        usersList,
        user,
        colors,
        datalists,
        eventCreationStep,
        setEventCreationStep,
        isDeleteAgendaEventModalOpen,
        setIsDeleteAgendaEventModalOpen,
        handleDeleteAgendaEvent,
        getEventIcs,
        handleStatusEvent
    };
};
